import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { createContentFromTemplate, listTemplates } from '@/data/sources-social';
import { aspectCss, channelLabel, layoutByKey } from '@/data/studio-layouts';
import type { Template } from '@/types/social';

/* Reusable workspace templates (bc_social_templates). Curated BC-owned layouts;
 * brand values resolve live at render from the Brand Kit. Cards are interactive:
 * Preview shows the layout/structure; "Use Template" creates a NEW editable
 * draft populated from the template and opens the studio editor — the original
 * template is never overwritten. Templates without a real structure are labelled
 * honestly rather than pretending to be usable. */

export function TemplatesPage() {
  const q = useQuery({ queryKey: ['social-templates'], queryFn: listTemplates });
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Template | null>(null);
  const [error, setError] = useState<string | null>(null);

  const use = useMutation({
    mutationFn: (t: Template) => createContentFromTemplate(t),
    onSuccess: (item) => { setPreview(null); navigate(`/social/content-studio/${item.id}`); },
    onError: () => setError('Could not create a draft from that template. Try again.'),
  });

  const items = q.data?.items ?? [];
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Templates" subtitle="Reusable layout + copy definitions. Brand colours, fonts and logo apply automatically at render." actions={<Badge tone="ai">Social</Badge>} />
      <EngineKeyline className="mb-6" />
      {error && <p className="mb-3 text-xs text-danger" role="alert">{error}</p>}
      {q.isLoading ? (
        <LoadingState rows={3} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="No templates yet" teach="Save a design as a template to reuse it — from the studio Layout step, choose a layout and 'Save as workspace template'. The studio's built-in layouts are always available when you create content." />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => {
            const usable = !!t.structure && t.structure.layoutKeys.length > 0;
            const pageCount = t.structure?.layoutKeys.length ?? 0;
            return (
              <Card key={t.id} className="flex flex-col p-4">
                <p className="text-sm font-medium text-text-primary">{t.name}</p>
                <p className="mt-1 text-xs text-text-secondary">{layoutByKey(t.layoutKey)?.name ?? (t.layoutKey || 'Custom')}</p>
                <div className="mt-2 flex items-center gap-2 text-[11px] text-text-tertiary">
                  {t.channel ? <span>{channelLabel(t.channel)}</span> : <span>Any channel</span>}
                  {usable ? <><span>·</span><span>{pageCount} page(s)</span></> : <><span>·</span><span className="text-warning">No layout yet</span></>}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="ai" onClick={() => use.mutate(t)} disabled={!usable || (use.isPending && use.variables?.id === t.id)}>
                    {use.isPending && use.variables?.id === t.id ? 'Creating…' : 'Use Template'}
                  </Button>
                  <Button variant="secondary" onClick={() => setPreview(t)}>Preview</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Template preview" onClick={() => setPreview(null)}>
          <Card className="w-full max-w-2xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-base font-semibold text-text-primary">{preview.name}</p>
              <Button variant="secondary" onClick={() => setPreview(null)}>Close</Button>
            </div>
            <TemplateVisualPreview template={preview} />
            <div className="mt-4 flex justify-end">
              <Button variant="ai" onClick={() => use.mutate(preview)} disabled={!preview.structure || preview.structure.layoutKeys.length === 0 || use.isPending}>
                {use.isPending ? 'Creating…' : 'Use Template'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ---- Real visual template preview -----------------------------------------
 * Renders the template as it will actually look: each page at its true aspect
 * ratio, with the layout's zones (media placeholder, logo, headline, body, CTA)
 * arranged per layout, representative copy, and page count. Brand colours, fonts
 * and the real logo resolve live at render in the editor; the preview marks the
 * brand-bound zones honestly rather than inventing a specific brand look. */
function TemplateVisualPreview({ template }: { template: Template }) {
  const struct = template.structure;
  const layoutKeys = struct?.layoutKeys?.length ? struct.layoutKeys : template.layoutKey ? [template.layoutKey] : [];
  const channel = template.channel ?? 'generic';
  const aspect = struct?.defaultAspect ?? 'square';
  const ratio = aspectCss(channel, aspect);
  const useLogo = struct?.brandBindings?.useLogo ?? true;

  if (layoutKeys.length === 0) {
    return (
      <div className="rounded-md border border-border-subtle bg-inset p-6 text-center text-xs text-warning">
        This template has no layout yet, so there is nothing to preview. Add a layout in the studio first.
      </div>
    );
  }

  const bodyLine = template.bodyTemplate?.trim().split('\n').filter(Boolean)[0] ?? '';
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-text-tertiary">
        <span>{channel === 'generic' ? 'Any channel' : channelLabel(channel)} · {aspect}</span>
        <span>{layoutKeys.length} page{layoutKeys.length === 1 ? '' : 's'}</span>
      </div>
      <div className="mt-2 flex gap-3 overflow-x-auto pb-2">
        {layoutKeys.map((lk, i) => (
          <div key={i} className="shrink-0">
            <SlidePreview layoutKey={lk} ratio={ratio} useLogo={useLogo} headline={template.name} body={i === 0 ? bodyLine : ''} index={i} total={layoutKeys.length} />
            <p className="mt-1 max-w-[180px] truncate text-center text-[10px] text-text-tertiary">{layoutByKey(lk)?.name ?? lk}</p>
          </div>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-text-tertiary">Representative preview — your Brand Kit colours, fonts and logo apply automatically when you edit. No media is placed until you choose it in the Media step.</p>
    </div>
  );
}

function SlidePreview({ layoutKey, ratio, useLogo, headline, body, index, total }: { layoutKey: string; ratio: number; useLogo: boolean; headline: string; body: string; index: number; total: number }) {
  const slots = layoutByKey(layoutKey)?.slots ?? [];
  const has = (s: string) => (slots as string[]).includes(s);
  const fullBleed = layoutKey === 'hero-media' || layoutKey === 'carousel-cover';
  const split = layoutKey === 'split-media-text';
  const showLogo = useLogo && has('logo');
  const width = 176; // px basis; height derived from the true aspect ratio
  const style = { width, aspectRatio: String(ratio || 1) };

  const Logo = showLogo ? (
    <span className="absolute left-2 top-2 z-10 rounded bg-surface/90 px-1.5 py-0.5 text-[8px] font-semibold text-text-secondary shadow-sm">LOGO</span>
  ) : null;
  const MediaBlock = (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-inset to-border-subtle text-[9px] text-text-tertiary">
      ▣ media
    </div>
  );
  const Headline = has('headline') ? (
    <p className="line-clamp-2 text-[10px] font-bold leading-tight text-text-primary">{headline || 'Your headline'}</p>
  ) : null;
  const Body = has('body') ? (
    body
      ? <p className="line-clamp-2 text-[8px] leading-tight text-text-secondary">{body}</p>
      : <div className="space-y-0.5"><div className="h-1 w-full rounded bg-border-subtle" /><div className="h-1 w-4/5 rounded bg-border-subtle" /></div>
  ) : null;
  const Cta = has('cta') ? (
    <span className="inline-block rounded-full bg-ai px-2 py-0.5 text-[8px] font-semibold text-white">Book now</span>
  ) : null;
  const Contact = has('contact') ? (
    <p className="text-[7px] text-text-tertiary">yourbusiness.com · @handle</p>
  ) : null;

  return (
    <div className="relative overflow-hidden rounded-md border border-border-default bg-canvas" style={style} aria-label={`Slide ${index + 1} of ${total}`}>
      {Logo}
      {fullBleed && has('media') ? (
        <>
          <div className="absolute inset-0">{MediaBlock}</div>
          <div className="absolute inset-x-0 bottom-0 space-y-1 bg-gradient-to-t from-black/70 to-transparent p-2">
            <div className="[&_*]:!text-white">{Headline}</div>
            {Cta}
          </div>
        </>
      ) : split && has('media') ? (
        <div className="flex h-full">
          <div className="w-1/2">{MediaBlock}</div>
          <div className="flex w-1/2 flex-col justify-center gap-1 p-2">{Headline}{Body}{Cta}{Contact}</div>
        </div>
      ) : has('media') ? (
        <div className="flex h-full flex-col">
          <div className="h-1/2">{MediaBlock}</div>
          <div className="flex flex-1 flex-col justify-center gap-1 p-2">{Headline}{Body}{Cta}</div>
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-1 p-3 text-center">{Headline}{Body}{Cta}{Contact}</div>
      )}
      {total > 1 && (
        <span className="absolute bottom-1 right-1 rounded bg-surface/80 px-1 text-[7px] text-text-tertiary">{index + 1}/{total}</span>
      )}
    </div>
  );
}
